import categoriesData from '../content/categories.json';
import articlesIndex from '../content/articles/index.json';
import guidesIndex from '../content/guides/index.json';
import ebooksIndex from '../content/ebooks/index.json';
import masterFaqsData from '../content/faqs/master.json';
import searchIndex from '../content/search-index.json';
import site from '../content/site.json';
import { resolveKcArticleImage } from '../components/kcArticleImages';
import {
  KC_ARTICLE_BASE,
  KC_DICTIONARY_BASE,
  KC_EBOOK_BASE,
  KC_FAQS_PATH,
  KC_GUIDE_BASE,
  KC_HOME_PATH,
  KC_NAVIGATOR_PATH,
  KC_SEARCH_PATH,
  KC_TOOLS_PATH,
  KC_TOPIC_BASE,
  DEFAULT_EBOOK_SLUG,
  articlePath,
  ebookChapterPath,
  ebookPath,
  formatReadTime,
  guidePath,
  navigatorPackagePath,
  topicPath,
} from './paths';
import {
  getNavigatorCatalog,
  getNavigatorLibraryStats,
  getNavigatorJourney,
  getNavigatorPackage,
  getNavigatorPackageBySlug,
  getRelatedNavigatorPackages,
  getSiblingNavigatorPackages,
  resolveNavigatorRouteFromHref,
  listNavigatorJourneys,
  listNavigatorObjectives,
  listNavigatorPackages,
  listNavigatorRoles,
} from './navigator';

const KC_SEO_AUTHOR = 'Charlee.ai';
const DICTIONARY_TERM_SET_NAME = 'Insurance AI Dictionary & Reference Guide';

function trimSeoText(value) {
  if (value == null || typeof value !== 'string') return '';
  return value.trim();
}

function parseContentDateLabel(label) {
  const text = trimSeoText(label);
  if (!text) return '';

  const monthYear = text.match(
    /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/i
  );
  if (monthYear) {
    const monthIndex = new Date(`${monthYear[1]} 1, 2000`).getMonth();
    const year = Number(monthYear[2]);
    if (Number.isFinite(monthIndex) && Number.isFinite(year)) {
      return new Date(Date.UTC(year, monthIndex, 1)).toISOString();
    }
  }

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
}

function buildSeoKeywords(seo = {}) {
  const parts = [];
  const primary = trimSeoText(seo.primaryKeyword);
  if (primary) parts.push(primary);
  const secondary = trimSeoText(seo.secondaryKeywords);
  if (secondary) {
    secondary
      .split(/[;,]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => parts.push(part));
  }
  return [...new Set(parts)].join(', ');
}

function extractFaqItems(sections = []) {
  const items = [];
  sections.forEach((section) => {
    if (section?.variant !== 'faq') return;
    (section.faqs || []).forEach((faq) => {
      const question = trimSeoText(String(faq?.question || '')).replace(
        /^\s*\d+[.)]\s+/,
        ''
      );
      const answer = trimSeoText(String(faq?.answer || ''));
      if (question && answer) items.push({ question, answer });
    });
  });
  return items;
}

function breadcrumbsToSeoItems(breadcrumbs = []) {
  return breadcrumbs
    .filter((crumb) => crumb?.label)
    .map((crumb) => ({
      name: crumb.label,
      path: crumb.path || undefined,
    }));
}

function resolveGuideOgImage(slug, seo = {}, sections = []) {
  const explicit = trimSeoText(seo.ogImage);
  if (explicit) {
    if (/^https?:\/\//i.test(explicit) || explicit.startsWith('/')) {
      return explicit;
    }
    return resolveKcArticleImage(explicit) || explicit;
  }

  const bySlug = resolveKcArticleImage(`glossary/${slug}/${slug}.png`);
  if (bySlug) return bySlug;

  for (const section of sections) {
    const key = trimSeoText(section?.image);
    if (!key) continue;
    const resolved = resolveKcArticleImage(key);
    if (resolved) return resolved;
  }

  return '';
}

export {
  KC_ARTICLE_BASE,
  KC_DICTIONARY_BASE,
  KC_EBOOK_BASE,
  KC_FAQS_PATH,
  KC_GUIDE_BASE,
  KC_HOME_PATH,
  KC_NAVIGATOR_PATH,
  KC_SEARCH_PATH,
  KC_TOOLS_PATH,
  KC_TOPIC_BASE,
  DEFAULT_EBOOK_SLUG,
  articlePath,
  ebookChapterPath,
  ebookPath,
  formatReadTime,
  guidePath,
  topicPath,
  getNavigatorCatalog,
  getNavigatorLibraryStats,
  getNavigatorJourney,
  getNavigatorPackage,
  getNavigatorPackageBySlug,
  getRelatedNavigatorPackages,
  getSiblingNavigatorPackages,
  resolveNavigatorRouteFromHref,
  listNavigatorJourneys,
  listNavigatorObjectives,
  listNavigatorPackages,
  listNavigatorRoles,
  navigatorPackagePath,
};

const categoryFaqModules = require.context(
  '../content/faqs/by-category',
  false,
  /\.json$/
);

const articlesById = new Map(articlesIndex.map((article) => [article.id, article]));
const articlesBySlug = new Map(articlesIndex.map((article) => [article.slug, article]));
const guidesBySlug = new Map(guidesIndex.map((guide) => [guide.slug, guide]));
const ebooksBySlug = new Map(ebooksIndex.map((book) => [book.slug, book]));
const categoriesById = new Map(categoriesData.map((category) => [category.id, category]));

function loadCategoryFaqs(slug) {
  try {
    return categoryFaqModules(`./${slug}.json`);
  } catch {
    return [];
  }
}

function enrichCategory(category) {
  if (!category) return null;
  const articles = getArticlesByCategoryId(category.id);
  return {
    ...category,
    path: topicPath(category.id),
    articleCount: articles.length,
  };
}

export function getSiteConfig() {
  return site;
}

export function getRedirects() {
  return site.redirects || [];
}

export function listCategories() {
  return categoriesData.map(enrichCategory);
}

export function getCategoryBySlug(slug) {
  return enrichCategory(categoriesById.get(slug) || null);
}

export function getClickableCategories() {
  return listCategories().filter((category) => category.clickable);
}

export function listArticles() {
  return articlesIndex;
}

export function getArticleMetaById(id) {
  return articlesById.get(id) || null;
}

export function getArticleMetaBySlug(slug) {
  return articlesBySlug.get(slug) || null;
}

export function getArticlesByCategoryId(categoryId) {
  return articlesIndex.filter((article) => article.categoryId === categoryId);
}

export async function getArticleBySlug(slug) {
  if (!articlesBySlug.has(slug)) return null;
  try {
    const mod = await import(
      /* webpackChunkName: "kc-article-[request]" */
      `../content/articles/by-slug/${slug}.json`
    );
    return mod.default || mod;
  } catch {
    return null;
  }
}

export function listGuides() {
  return guidesIndex;
}

export function getGuideMetaBySlug(slug) {
  return guidesBySlug.get(slug) || null;
}

export async function getGuideBySlug(slug) {
  if (!guidesBySlug.has(slug)) return null;
  try {
    const mod = await import(
      /* webpackChunkName: "kc-guide-[request]" */
      `../content/guides/by-slug/${slug}.json`
    );
    return mod.default || mod;
  } catch {
    return null;
  }
}

export async function buildGuidePage(slug) {
  const guide = await getGuideBySlug(slug);
  if (!guide) return null;

  const path = guidePath(guide.slug);
  const readTime = formatReadTime(guide.readTimeMinutes);

  const sections = (guide.sections || [])
    .filter((section) => {
      const title = String(section.title || '');
      if (/^seo\b/i.test(title) || /aeo metadata/i.test(title)) return false;
      if (/seo and aeo/i.test(title)) return false;
      if (/^publication header$/i.test(title)) return false;
      if (/^publication checklist$/i.test(title)) return false;
      if (/^publication quality checklist$/i.test(title)) return false;
      return true;
    })
    .slice();

  // Ensure FAQ sections render last.
  const faqs = sections.filter((section) => section.variant === 'faq');
  const nonFaqs = sections.filter((section) => section.variant !== 'faq');
  const orderedSections = [...nonFaqs, ...faqs];

  const relatedGuides = (guide.relatedGuideSlugs || [])
    .map((guideSlug) => getGuideMetaBySlug(guideSlug))
    .filter(Boolean)
    .map((item) => ({
      id: item.id,
      title: item.title,
      summary: item.summary,
      path: guidePath(item.slug),
      readTime: formatReadTime(item.readTimeMinutes),
    }));

  const relatedFromArticles = (guide.relatedArticleSlugs || [])
    .map((articleSlug) => getArticleMetaBySlug(articleSlug))
    .filter(Boolean)
    .map((article) => ({
      id: article.id,
      title: article.title,
      summary: article.summary,
      path: articlePath(article.slug),
      readTime: formatReadTime(article.readTimeMinutes),
    }));

  const h1 =
    trimSeoText(guide.seo?.recommendedH1) ||
    trimSeoText(guide.title) ||
    'Guide';
  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Knowledge Center', path: KC_HOME_PATH },
    { label: 'Reference Guides', path: KC_GUIDE_BASE },
    { label: guide.title, path },
  ];
  const metaDescription =
    trimSeoText(guide.seo?.metaDescription) ||
    trimSeoText(guide.seo?.featuredSnippet) ||
    trimSeoText(guide.summary) ||
    '';
  const contentDate =
    parseContentDateLabel(guide.lastUpdated) || undefined;
  const faqItems = extractFaqItems(orderedSections);
  const ogImage = resolveGuideOgImage(guide.slug, guide.seo, orderedSections);

  return {
    ...guide,
    sections: orderedSections,
    path,
    categoryPath: KC_HOME_PATH,
    readTime,
    h1,
    audience: guide.publication?.audience || 'Insurance Professionals',
    breadcrumbs,
    seo: {
      pageTitle:
        guide.seo?.pageTitle || `${guide.title} | Charlee.ai Knowledge Center`,
      metaDescription,
      canonicalPath: path,
      ogType: 'article',
      ogImage: ogImage || undefined,
      keywords: buildSeoKeywords(guide.seo) || undefined,
      articlePublishedTime: contentDate,
      articleModifiedTime: contentDate,
      author: KC_SEO_AUTHOR,
      authorType: 'Organization',
      headline: h1,
      breadcrumbItems: breadcrumbsToSeoItems(breadcrumbs),
      faqItems: faqItems.length ? faqItems : undefined,
      definedTerm: {
        name: guide.title,
        description: metaDescription,
        termSetName:
          guide.publication?.series || DICTIONARY_TERM_SET_NAME,
        termSetUrl: KC_DICTIONARY_BASE,
      },
    },
    related: [...relatedGuides, ...relatedFromArticles].slice(0, 6),
  };
}

export function listEbooks() {
  return ebooksIndex;
}

export function getEbookMetaBySlug(slug) {
  return ebooksBySlug.get(slug) || null;
}

export async function getEbookBySlug(slug) {
  if (!ebooksBySlug.has(slug)) return null;
  try {
    const mod = await import(
      /* webpackChunkName: "kc-ebook-[request]" */
      `../content/ebooks/by-slug/${slug}.json`
    );
    return mod.default || mod;
  } catch {
    return null;
  }
}

export async function getEbookChapterBySlug(chapterSlug) {
  try {
    const mod = await import(
      /* webpackChunkName: "kc-ebook-chapter-[request]" */
      `../content/ebooks/chapters/by-slug/${chapterSlug}.json`
    );
    return mod.default || mod;
  } catch {
    return null;
  }
}

export async function buildEbookPage(slug = DEFAULT_EBOOK_SLUG) {
  const book = await getEbookBySlug(slug);
  if (!book) return null;

  const path = ebookPath(book.slug);
  const h1 =
    trimSeoText(book.seo?.recommendedH1) ||
    trimSeoText(book.title) ||
    'Ebook';
  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Knowledge Center', path: KC_HOME_PATH },
    { label: 'Ebook', path },
  ];
  const metaDescription =
    trimSeoText(book.seo?.metaDescription) ||
    trimSeoText(book.summary) ||
    '';
  const contentDate = parseContentDateLabel(book.lastUpdated) || undefined;

  return {
    ...book,
    path,
    h1,
    readTime: book.readTimeLabel || formatReadTime(book.readTimeMinutes),
    breadcrumbs,
    seo: {
      pageTitle:
        book.seo?.pageTitle ||
        `${book.title} | Charlee.ai Knowledge Center`,
      metaDescription,
      canonicalPath: path,
      keywords:
        buildSeoKeywords(book.seo) ||
        [book.title, 'document intelligence', 'P&C insurance', 'insurance AI']
          .filter(Boolean)
          .join(', '),
      articleModifiedTime: contentDate,
      author: KC_SEO_AUTHOR,
      authorType: 'Organization',
      headline: h1,
      breadcrumbItems: breadcrumbsToSeoItems(breadcrumbs),
      book: {
        name: book.title,
        numberOfPages: book.chapterCount || undefined,
      },
    },
  };
}

export async function buildEbookChapterPage(bookSlug, chapterSlug) {
  const book = await getEbookBySlug(bookSlug || DEFAULT_EBOOK_SLUG);
  const chapter = await getEbookChapterBySlug(chapterSlug);
  if (!book || !chapter) return null;
  if (chapter.bookSlug && chapter.bookSlug !== book.slug) return null;

  const path = ebookChapterPath(book.slug, chapter.slug);
  const bookPath = ebookPath(book.slug);
  const readTime = formatReadTime(chapter.readTimeMinutes);

  const prev = chapter.prevSlug
    ? {
        slug: chapter.prevSlug,
        path: ebookChapterPath(book.slug, chapter.prevSlug),
        title:
          book.chapters?.find((item) => item.slug === chapter.prevSlug)?.title ||
          'Previous chapter',
      }
    : null;
  const next = chapter.nextSlug
    ? {
        slug: chapter.nextSlug,
        path: ebookChapterPath(book.slug, chapter.nextSlug),
        title:
          book.chapters?.find((item) => item.slug === chapter.nextSlug)?.title ||
          'Next chapter',
      }
    : null;

  const h1 =
    trimSeoText(chapter.seo?.recommendedH1) ||
    trimSeoText(chapter.title) ||
    'Chapter';
  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Knowledge Center', path: KC_HOME_PATH },
    { label: 'Ebook', path: bookPath },
    { label: `Chapter ${chapter.chapterNumber}`, path },
  ];
  const metaDescription =
    trimSeoText(chapter.seo?.metaDescription) ||
    trimSeoText(chapter.summary) ||
    '';
  const contentDate =
    parseContentDateLabel(chapter.lastUpdated || book.lastUpdated) ||
    undefined;
  const faqItems = extractFaqItems(chapter.sections);

  return {
    ...chapter,
    bookTitle: book.title,
    bookSubtitle: book.subtitle,
    path,
    bookPath,
    readTime,
    h1,
    audience: book.audience || 'Insurance Professionals',
    breadcrumbs,
    publication: {
      series: book.documentTitle || 'Executive Guide to Document Intelligence',
      category: chapter.partTitle,
      learningPath: `Chapter ${chapter.chapterNumber} of ${book.chapterCount}`,
      chapterPositioning: chapter.subtitle || '',
      audience: book.audience,
    },
    guideSubtitle: chapter.subtitle,
    seo: {
      pageTitle:
        chapter.seo?.pageTitle ||
        `${chapter.title} | ${book.title} | Charlee.ai`,
      metaDescription,
      canonicalPath: path,
      ogType: 'article',
      keywords:
        buildSeoKeywords(chapter.seo) ||
        [
          chapter.title,
          book.title,
          'document intelligence',
          'P&C insurance',
        ]
          .filter(Boolean)
          .join(', '),
      articlePublishedTime: contentDate,
      articleModifiedTime: contentDate,
      author: KC_SEO_AUTHOR,
      authorType: 'Organization',
      headline: h1,
      breadcrumbItems: breadcrumbsToSeoItems(breadcrumbs),
      faqItems: faqItems.length ? faqItems : undefined,
      chapter: {
        name: chapter.title,
        position: chapter.chapterNumber,
        bookName: book.title,
        bookUrl: bookPath,
      },
    },
    prev,
    next,
  };
}

export function getFeaturedEbook() {
  const section = site.featuredEbook;
  if (!section) return null;
  const meta = getEbookMetaBySlug(section.slug || DEFAULT_EBOOK_SLUG);
  return {
    ...section,
    chapterCount: meta?.chapterCount,
    partCount: meta?.partCount,
    readTimeLabel: meta?.readTimeLabel,
    difficulty: meta?.difficulty,
    lastUpdated: meta?.lastUpdated,
    href: ebookPath(section.slug || DEFAULT_EBOOK_SLUG),
    firstChapterHref: meta?.firstChapterSlug
      ? ebookChapterPath(section.slug || DEFAULT_EBOOK_SLUG, meta.firstChapterSlug)
      : section.secondaryCta?.href,
  };
}

export function getRelatedArticles(ids = []) {
  return ids
    .map((id) => getArticleMetaById(id))
    .filter(Boolean)
    .map((article) => ({
      ...article,
      path: articlePath(article.slug),
      readTime: formatReadTime(article.readTimeMinutes),
    }));
}

export function getMasterFaqs() {
  return masterFaqsData;
}

export function getHomepageFaqs(limit = 6) {
  return masterFaqsData.slice(0, limit);
}

export function getCategoryFaqs(slug) {
  return loadCategoryFaqs(slug);
}

export function buildCategoryPage(slug) {
  const category = getCategoryBySlug(slug);
  if (!category) return null;

  const articles = getArticlesByCategoryId(category.id);
  if (!articles.length) return null;

  const faqs = getCategoryFaqs(category.id);
  const readMinutes = articles
    .map((article) => article.readTimeMinutes)
    .filter((n) => Number.isFinite(n));
  const avgMinutes = readMinutes.length
    ? Math.round(readMinutes.reduce((sum, n) => sum + n, 0) / readMinutes.length)
    : null;

  return {
    slug: category.id,
    path: category.path,
    seo: {
      pageTitle: `${category.longTitle} | Knowledge Center | Charlee.ai`,
      metaDescription: category.description,
      canonicalPath: category.path,
    },
    title: category.longTitle,
    description: category.description,
    icon: category.icon,
    articleCountLabel: `${articles.length} Articles`,
    avgReadTimeLabel: avgMinutes ? `${avgMinutes} min avg read` : '8-12 min avg read',
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'Knowledge Center', path: KC_HOME_PATH },
      { label: category.longTitle, path: null },
    ],
    topics: articles.map((article) => ({
      id: article.id,
      title: article.title,
      description: article.summary,
      icon: category.icon,
      articleCountLabel: formatReadTime(article.readTimeMinutes),
      path: articlePath(article.slug),
      anchorId: `kc-cat-article-${String(article.id).replace(/\./g, '-')}`,
    })),
    sidebarItems: [
      { id: 'overview', label: 'Overview', targetId: 'kc-category-overview' },
      ...articles.map((article) => ({
        id: article.id,
        label: article.title,
        targetId: `kc-cat-article-${String(article.id).replace(/\./g, '-')}`,
      })),
      ...(faqs.length
        ? [{ id: 'faqs', label: 'FAQs', targetId: 'kc-category-faqs' }]
        : []),
    ],
    faqs,
  };
}

export async function buildArticlePage(slug) {
  const article = await getArticleBySlug(slug);
  if (!article) return null;

  const category = getCategoryBySlug(article.categoryId);
  const path = articlePath(article.slug);
  const categoryPath = topicPath(article.categoryId);
  const readTime = formatReadTime(article.readTimeMinutes);

  return {
    ...article,
    path,
    categoryPath,
    readTime,
    audience: 'Insurance Professionals',
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'Knowledge Center', path: KC_HOME_PATH },
      {
        label: category?.longTitle || 'Topic',
        path: categoryPath,
      },
      { label: article.title, path: null },
    ],
    seo: {
      pageTitle: article.seo?.pageTitle || article.title,
      metaDescription: article.seo?.metaDescription || article.summary || '',
      canonicalPath: path,
    },
    related: getRelatedArticles(article.relatedArticleIds),
  };
}

const FEATURED_ARTICLE_IMAGES = {
  '1.1': 'category-1/1-1-ai-in-claims-operating-model.png',
  '1.2': 'category-1/1-1-ai-in-claims-operating-model.png',
};

export function getFeaturedArticles() {
  const categoryLabel = (categoryId) =>
    categoriesById.get(categoryId)?.shortTitle ||
    categoriesById.get(categoryId)?.longTitle ||
    'Knowledge Center';

  return (site.featuredArticleIds || [])
    .map((id) => getArticleMetaById(id))
    .filter(Boolean)
    .map((article) => ({
      id: article.id,
      title: article.title,
      summary: article.summary,
      tag: categoryLabel(article.categoryId),
      readTime: formatReadTime(article.readTimeMinutes),
      path: articlePath(article.slug),
      imageKey: FEATURED_ARTICLE_IMAGES[article.id] || null,
    }));
}

function guideCardTitle(guide) {
  const overrides = {
    'retrieval-augmented-generation': 'Retrieval-Augmented Generation',
    'ai-agents': 'AI Agents',
    'generative-ai': 'Generative AI',
    'predictive-ai': 'Predictive AI',
  };
  if (overrides[guide.slug]) return overrides[guide.slug];

  return String(guide.slug || '')
    .split('-')
    .map((part) => {
      if (part === 'ai') return 'AI';
      if (part === 'ml') return 'ML';
      if (part === 'nlp') return 'NLP';
      if (part === 'rag') return 'RAG';
      if (part === 'llms') return 'LLMs';
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ') || guide.title;
}

function listGuidesInDocOrder() {
  return [...guidesIndex].sort(
    (a, b) => (a.entryNumber || 0) - (b.entryNumber || 0)
  );
}

export function getFeaturedReferenceGuides() {
  const section = site.featuredReferenceGuides;
  if (!section) return null;

  const guides = listGuidesInDocOrder().map((guide) => ({
    id: guide.slug,
    title: guideCardTitle(guide),
    subtitle: guide.guideSubtitle || 'Executive Reference Guide',
    pages: guide.pageCount,
    updated: guide.lastUpdated ? `Updated ${guide.lastUpdated}` : '',
    icon: guide.icon,
    href: guidePath(guide.slug),
  }));

  return {
    ...section,
    guides,
  };
}

export function getDictionaryIndex() {
  const preview = site.dictionaryPreview || {};
  const guides = listGuidesInDocOrder();
  const groupsMap = new Map();

  const pushTerm = (letter, term, href, meta = {}) => {
    if (!groupsMap.has(letter)) groupsMap.set(letter, []);
    const existing = groupsMap.get(letter);
    if (existing.some((item) => item.term.toLowerCase() === term.toLowerCase())) {
      return;
    }
    existing.push({ term, href, ...meta });
  };

  guides.forEach((guide) => {
    const term = guideCardTitle(guide);
    const letter = term.charAt(0).toUpperCase();
    pushTerm(letter, term, guidePath(guide.slug), {
      slug: guide.slug,
      summary: guide.summary || '',
      subtitle: guide.guideSubtitle || '',
      entryNumber: guide.entryNumber,
    });
  });

  // Common alternate dictionary labels for A–Z browsing
  const aliases = [
    { term: 'Bias', slug: 'ai-bias' },
    { term: 'Context Window', slug: 'large-language-models' },
  ];
  aliases.forEach((alias) => {
    const letter = alias.term.charAt(0).toUpperCase();
    const guide = alias.slug ? getGuideMetaBySlug(alias.slug) : null;
    pushTerm(
      letter,
      alias.term,
      guide ? guidePath(guide.slug) : null,
      guide
        ? {
            slug: guide.slug,
            summary: guide.summary || '',
            subtitle: guide.guideSubtitle || '',
            entryNumber: guide.entryNumber,
          }
        : { summary: '', subtitle: '' }
    );
  });

  // Sort terms within each letter
  for (const [, terms] of groupsMap) {
    terms.sort((a, b) => a.term.localeCompare(b.term));
  }

  const groups = [...groupsMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([letter, terms]) => ({ letter, terms }));

  const letters =
    preview.letters ||
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  return {
    title: preview.title || 'Insurance AI Dictionary (A–Z)',
    description:
      preview.description ||
      'Browse executive reference definitions from the Insurance AI Dictionary.',
    letters,
    activeLetters: groups.map((group) => group.letter),
    groups,
    termCount: groups.reduce((sum, group) => sum + group.terms.length, 0),
  };
}

export function getDictionaryPreview() {
  const full = getDictionaryIndex();
  if (!full) return null;

  const previewLetters = ['A', 'B', 'C'];
  const groups = previewLetters.map((letter) => {
    const found = full.groups.find((group) => group.letter === letter);
    return {
      letter,
      terms: (found?.terms || []).slice(0, 6),
    };
  });

  return {
    ...full,
    previewLetters,
    activeLetters: previewLetters.filter((letter) =>
      groups.some((group) => group.letter === letter && group.terms.length > 0)
    ),
    groups,
  };
}

export function getPopularTopics() {
  return site.popularTopics || [];
}

export function getHeroConfig() {
  return site.hero;
}

export function getStats() {
  const baseStats = site.stats || [];
  const guides = listGuidesInDocOrder();
  const dictionary = getDictionaryIndex();

  return baseStats.map((stat) => {
    if (stat.id === 'guides') {
      return {
        ...stat,
        value: String(guides.length),
        label: stat.label || 'Reference Guides',
      };
    }
    if (stat.id === 'dictionary') {
      return {
        ...stat,
        value: String(dictionary.termCount || guides.length),
        label: stat.label || 'Dictionary Terms',
      };
    }
    return stat;
  });
}

export function getHomeSeo() {
  return site.seo.home;
}

export function getFaqsSeo() {
  return site.seo.faqs;
}

export function getSearchSeo() {
  return site.seo.search;
}

function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .trim();
}

function matchesQuery(query, fields) {
  const q = normalize(query);
  if (!q) return false;
  const tokens = q.split(/\s+/).filter(Boolean);
  const haystack = fields.map(normalize).filter(Boolean).join(' ');
  if (haystack.includes(q)) return true;
  return tokens.every((token) => haystack.includes(token));
}

function categoryLabel(categoryId) {
  const category = categoriesById.get(categoryId);
  return category?.shortTitle || category?.longTitle || 'Knowledge Center';
}

function toArticleResult(article) {
  return {
    type: 'article',
    id: `article-${article.id}`,
    title: article.title,
    breadcrumb: `Knowledge Center > ${categoryLabel(article.categoryId)}`,
    readTime: formatReadTime(article.readTimeMinutes),
    path: articlePath(article.slug),
  };
}

function toCategoryResult(category) {
  return {
    type: 'category',
    id: `category-${category.id}`,
    title: category.longTitle,
    breadcrumb: `Knowledge Center > ${category.shortTitle}`,
    path: topicPath(category.id),
  };
}

function toDictionaryResult(entry) {
  return {
    type: 'dictionary',
    id: `dictionary-${entry.slug || slugifySafe(entry.term)}`,
    title: entry.term || entry.title,
    breadcrumb: 'Knowledge Center > Insurance AI Dictionary',
    readTime: entry.readTimeMinutes
      ? formatReadTime(entry.readTimeMinutes)
      : null,
    path: entry.href || (entry.slug ? guidePath(entry.slug) : KC_DICTIONARY_BASE),
    summary: entry.summary || entry.subtitle || '',
  };
}

function slugifySafe(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function listDictionarySearchEntries() {
  const dictionary = getDictionaryIndex();
  const byKey = new Map();

  dictionary.groups.forEach((group) => {
    group.terms.forEach((term) => {
      const key = (term.slug || term.term).toLowerCase();
      if (byKey.has(key)) return;
      byKey.set(key, {
        term: term.term,
        slug: term.slug,
        href: term.href,
        summary: term.summary || '',
        subtitle: term.subtitle || '',
        readTimeMinutes: null,
      });
    });
  });

  listGuidesInDocOrder().forEach((guide) => {
    const key = guide.slug;
    const existing = byKey.get(key);
    byKey.set(key, {
      term: existing?.term || guideCardTitle(guide),
      slug: guide.slug,
      href: guidePath(guide.slug),
      summary: guide.summary || existing?.summary || '',
      subtitle: guide.guideSubtitle || existing?.subtitle || '',
      readTimeMinutes: guide.readTimeMinutes,
      title: guide.title,
      documentTitle: guide.documentTitle,
    });
  });

  return [...byKey.values()];
}

function toNavigatorResult(item) {
  const isTool = item.kind === 'tool' || item.resource_type === 'Tool';
  return {
    type: isTool ? 'tool' : 'navigator',
    id: `${isTool ? 'tool' : 'navigator'}-${item.slug}`,
    title: item.title,
    breadcrumb: isTool
      ? 'Knowledge Center > Tools'
      : 'Knowledge Center > Navigator',
    path: item.detailPath || navigatorPackagePath(item),
    summary: item.description || item.purpose || '',
  };
}

function listNavigatorSearchEntries() {
  return listNavigatorPackages({}).map(toNavigatorResult);
}

export function getAllKnowledgeCenterResults() {
  const categories = searchIndex.categories.map(toCategoryResult);
  const articles = searchIndex.articles.map(toArticleResult);
  const dictionary = listDictionarySearchEntries().map(toDictionaryResult);
  const navigatorTools = listNavigatorSearchEntries();
  const navigator = navigatorTools.filter((item) => item.type === 'navigator');
  const tools = navigatorTools.filter((item) => item.type === 'tool');
  return {
    query: '',
    articles,
    categories,
    dictionary,
    navigator,
    tools,
    all: [...categories, ...dictionary, ...articles],
  };
}

export function searchKnowledgeCenter(rawQuery) {
  const query = normalize(rawQuery);

  if (!query) {
    return getAllKnowledgeCenterResults();
  }

  const articles = searchIndex.articles
    .filter((article) =>
      matchesQuery(query, [
        article.title,
        article.summary,
        article.id,
        article.slug,
        article.categoryId,
        formatReadTime(article.readTimeMinutes),
        categoryLabel(article.categoryId),
      ])
    )
    .map(toArticleResult);

  const categories = searchIndex.categories
    .filter((category) =>
      matchesQuery(query, [
        category.longTitle,
        category.shortTitle,
        category.description,
        category.id,
      ])
    )
    .map(toCategoryResult);

  const dictionary = listDictionarySearchEntries()
    .filter((entry) =>
      matchesQuery(query, [
        entry.term,
        entry.title,
        entry.documentTitle,
        entry.summary,
        entry.subtitle,
        entry.slug,
        'dictionary',
        'glossary',
        'insurance ai dictionary',
      ])
    )
    .map(toDictionaryResult);

  // Surface articles whose FAQ questions match, without duplicating rows.
  const articleIds = new Set(articles.map((item) => item.id));
  searchIndex.faqs.forEach((faq) => {
    if (faq.scope !== 'article' || !faq.slug) return;
    if (!matchesQuery(query, [faq.question])) return;
    const meta = getArticleMetaBySlug(faq.slug);
    if (!meta) return;
    const result = toArticleResult(meta);
    if (!articleIds.has(result.id)) {
      articleIds.add(result.id);
      articles.push(result);
    }
  });

  const navigatorTools = listNavigatorPackages({})
    .filter((item) =>
      matchesQuery(query, [
        item.title,
        item.description,
        item.purpose,
        item.category,
        item.stage,
        item.slug,
        item.id,
        item.kind,
        ...(item.audiences || []),
        'navigator',
        'tools',
      ])
    )
    .map(toNavigatorResult);
  const navigator = navigatorTools.filter((item) => item.type === 'navigator');
  const tools = navigatorTools.filter((item) => item.type === 'tool');

  return {
    query,
    articles,
    categories,
    dictionary,
    navigator,
    tools,
    all: [...categories, ...dictionary, ...navigator, ...tools, ...articles],
  };
}
